import { z } from "zod";

export const FinancialAidRequirementStateSchema = z.enum([
  "hidden",
  "required",
  "optional",
]);
export type FinancialAidRequirementState = z.infer<
  typeof FinancialAidRequirementStateSchema
>;

export const FinancialAidRequirementPolicySchema = z
  .object({
    fieldRequirements: z
      .record(z.string(), FinancialAidRequirementStateSchema)
      .default({}),
  })
  .default({ fieldRequirements: {} });

export type FinancialAidRequirementPolicy = z.infer<
  typeof FinancialAidRequirementPolicySchema
>;

export type FinancialAidRequirementCondition = {
  kind: string;
  [key: string]: unknown;
};

export type FinancialAidRequirementField<
  Condition extends FinancialAidRequirementCondition = FinancialAidRequirementCondition,
> = {
  fieldKey: string;
  path: string;
  step: number;
  label: string;
  group: string;
  togglable: boolean;
  defaultRequirement: FinancialAidRequirementState;
  message?: string;
  condition: Condition;
};

export type FinancialAidRequirementFieldKeySource =
  | string
  | Pick<FinancialAidRequirementField, "fieldKey">;

const createAllowedFinancialAidRequirementKeySet = (
  allowedFields: readonly FinancialAidRequirementFieldKeySource[],
): Set<string> =>
  new Set(
    allowedFields.map((field) =>
      typeof field === "string" ? field : field.fieldKey,
    ),
  );

export const DEFAULT_FINANCIAL_AID_REQUIREMENT_PROFILE: FinancialAidRequirementPolicy = {
  fieldRequirements: {},
};

export const listUnknownFinancialAidRequirementKeys = (
  policy: FinancialAidRequirementPolicy | undefined,
  allowedFields: readonly FinancialAidRequirementFieldKeySource[],
): string[] => {
  const allowedKeys = createAllowedFinancialAidRequirementKeySet(allowedFields);
  return Object.keys(policy?.fieldRequirements ?? {}).filter(
    (fieldKey) => !allowedKeys.has(fieldKey),
  );
};

export const createFinancialAidRequirementPolicySchema = (
  allowedFields: readonly FinancialAidRequirementFieldKeySource[],
) =>
  FinancialAidRequirementPolicySchema.superRefine((policy, ctx) => {
    for (const fieldKey of listUnknownFinancialAidRequirementKeys(
      policy,
      allowedFields,
    )) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unknown Financial Aid requirement field "${fieldKey}".`,
        path: ["fieldRequirements", fieldKey],
      });
    }
  });

export const resolveFinancialAidRequirementState = (
  policy: FinancialAidRequirementPolicy | undefined,
  field: FinancialAidRequirementField,
): FinancialAidRequirementState =>
  policy?.fieldRequirements?.[field.fieldKey] ?? field.defaultRequirement;
